/*
  Loading and placement of the precomputed isochrone grid (public/isochrones/parsec.json,
  generated by scripts/generate_isochrones.py). The file stores absolute magnitudes
  per filter on a [Fe/H] x log(age) grid; placing an isochrone on the observed CMD
  is a shift by distance modulus and reddening.
*/

// Extinction coefficients A_band = R_band * E(B-V) for the Pan-STARRS1 bands the
// asset is generated in (Schlafly & Finkbeiner 2011 Table 6, R_V = 3.1)
export const REDDENING_COEFFICIENTS = { gp: 3.172, rp: 2.271, ip: 1.682, zs: 1.322 }

// export these constants so the slider controls can use them as limits and snap to them
export const MU_MIN = 0
export const MU_MAX = 18
export const MU_STEP = 0.05

let gridPromise = null

// fetch the grid once per app session; concurrent callers share the same promise
export function loadIsochroneGrid() {
  if (!gridPromise) {
    gridPromise = fetch('/isochrones/parsec.json').then((response) => {
      if (!response.ok) {
        throw new Error(`isochrone grid fetch failed (${response.status})`)
      }
      return response.json()
    })
    // a failed load (offline, bad deploy) may be retried on the next mount
    gridPromise.catch(() => { gridPromise = null })
  }
  return gridPromise
}

export function nearestNode(nodes, value) {
  return nodes.reduce((best, node) => (Math.abs(node - value) < Math.abs(best - value) ? node : best))
}

// the fitting sliders snap to grid nodes, so an exact-match lookup after snapping
export function findIsochrone(grid, feh, logAge) {
  const snappedFeh = nearestNode(grid.feh_nodes, feh)
  const snappedLogAge = nearestNode(grid.log_age_nodes, logAge)
  return grid.isochrones.find((iso) => iso.feh === snappedFeh && iso.log_age === snappedLogAge) || null
}

/*
  Apparent-CMD polyline of an isochrone for a given distance modulus mu and
  reddening E(B-V):
    x (color)     = (M_blue - M_red) + (R_blue - R_red) * E(B-V)
    y (magnitude) = M_red + mu + R_red * E(B-V)
*/
export function placeIsochrone(isochrone, blueFilter, redFilter, mu, ebv) {
  const blueMags = isochrone.mags[blueFilter]
  const redMags = isochrone.mags[redFilter]
  if (!blueMags || !redMags) return null
  const colorShift = (REDDENING_COEFFICIENTS[blueFilter] - REDDENING_COEFFICIENTS[redFilter]) * ebv
  const magShift = mu + REDDENING_COEFFICIENTS[redFilter] * ebv
  return redMags.map((redMag, index) => ({
    x: (blueMags[index] - redMag) + colorShift,
    y: redMag + magShift
  }))
}

// Converts distance modulus to parsecs: inverse of mu = 5 * log10(distance / 10 pc)
export function distanceParsecs(mu) {
  return Math.pow(10, (mu + 5) / 5)
}

// snap a distance modulus to the ranges and steps allowed
export function snapDistanceModulus(mu) {
  const clamped = Math.min(Math.max(mu, MU_MIN), MU_MAX)
  const stepped = Math.round(clamped / MU_STEP) * MU_STEP
  return Math.round(stepped * 100) / 100
}

// Converts parsecs to distance modulus: mu = 5 * log10(distance / 10 pc); null when the distance is unusable
export function distanceModulusFromParsecs(parsecs) {
  if (!Number.isFinite(parsecs) || parsecs <= 0) return null
  return 5 * Math.log10(parsecs) - 5
}

// starting guess for mu from a Gaia parallax (mas); null when parallax is unusable
export function distanceModulusFromParallax(parallaxMas) {
  if (!Number.isFinite(parallaxMas) || parallaxMas <= 0) return null
  return distanceModulusFromParsecs(1000 / parallaxMas)
}
